/**
 * Utility global-search-brain-utility
 * @description Brain utility for global-search-module
 * @copyright 2021-7-8
 * @file
 */

const unauthPath = '/search/public/results/';
const securePath = /\/\/secure|\/\/bills/;
const loginPrefix = '/login/sign-in/signOnV2Screen.go?requestFrom=search&reason=';
const brainPrefix = '/myaccounts/brain/redirect.go?requestFrom=search&target=';
const searchAffix = '&searchText=';
const brainProtocol = 'brain:';

/**
 * The GlobalSearchBrainUtility.
 * This utility is responsible for providing common methods for brain redirects.
 * @export
 * @class
 */
class GlobalSearchBrainUtility {
  /**
   * Creates an instance of GlobalSearchBrainUtility.
   * @param {object} params - used to initialize utility.
   * @memberof GlobalSearchBrainUtility
   */
  constructor(params) {
    this.isAuth = params && params.isAuth;
    this.moduleInstance = params && params.moduleInstance;
    if (this.moduleInstance.Widget && this.moduleInstance.Widget.siteParams) {
      if (this.isAuth) {
        this.secureHost = this.moduleInstance.Widget.siteParams.secureHost;
      } else {
        this.wwwHost = this.moduleInstance.Widget.siteParams.wwwHost;
      }
    }
  }

  /**
   * Checks if the passed url is Brain or not.
   * @param {string} url - URL of page.
   * @returns {boolean} - returns true if it's Brain URL.
   */
  isBrain(url) {
    if (!url) {
      return false;
    }
    return url.indexOf(brainProtocol) === 0;
  }

  /**
   * Returns true/false based on authentication and validating current and target urls
   * @param {string} current - current URL.
   * @param {string} future - future URL.
   * @returns {boolean} - returns true/false.
   */
  checkAuth(current, future) {
    return typeof current === 'string' && (current.match(securePath) || this.isAuth) && !current.match(unauthPath) && typeof future === 'string' && future.length > 0 && !future.match(securePath);
  }

  /**
   * Attaches value to target attribute of anchor link based on checkAuth()
   * @param {string} url - URL of page.
   * @returns {boolean} - returns true/false.
   */
  newWindow(url) {
    return this.checkAuth(window.location.href, url) ? '_blank' : '';
  }

  /**
   * Returns the complete url with search query if available
   * @param {string} target - target element.
   * @param {string} query - page query string.
   * @returns {string} - returns the complete url with search query if available.
   */
  fullUrl(target, query) {
    const urlHost = this.isAuth ? this.secureHost : this.wwwHost;
    const prefix = this.isAuth ? brainPrefix : loginPrefix;
    const qs = typeof query === 'string' && !this.isAuth ? searchAffix + query : '';
    return `${urlHost}${prefix}${target.replace(/^brain:/, '')}${qs}`;
  }

  /**
   * Toggles link based on auth/unauth and returns updated anchor link
   * @param {object} link - hyperlink object.
   * @param {string} [forceTo] - forcefully override the natural toggle.
   * @returns {string} - returns the complete URL with search query if available.
   */
  toggleUrl(link, forceTo = '') {
    const href = link.attr('href');
    let toggleTo = href.match(loginPrefix) ? 'auth' : 'unauth';
    // If we are forcing a value, override the natural toggle.
    if (forceTo) {
      toggleTo = forceTo;
    }
    if (toggleTo === 'auth') {
      link.attr('href', href.replace(loginPrefix, brainPrefix));
      if (link.attr('href').match('/myaccounts/brain/')) {
        link.attr('target', '');
      }
    } else {
      link.attr('href', href.replace(brainPrefix, loginPrefix));
    }
    return link;
  }
}

export { GlobalSearchBrainUtility as default };
